import React from 'react';

export type UsageFeature = {
  label: string;
  limit: number | null;
  used: number;
  remaining: number | null;
  unlimited: boolean;
  coins: number;
};

export type UsageSummary = {
  plan: 'free' | 'basic' | 'premium' | string;
  scope: 'lifetime' | 'daily' | 'monthly' | string;
  dateKey: string;
  coins?: Record<string, number>;
  features: Record<string, UsageFeature>;
};

type Props = {
  usage: UsageSummary | null;
  loading: boolean;
};

const planLabels: Record<string, string> = {
  free: '無料お試し',
  basic: '基本プラン',
  premium: 'プレミアムプラン',
  admin: '管理者',
};

const scopeLabels: Record<string, string> = {
  lifetime: '累計',
  daily: '本日',
  monthly: '今月',
  unlimited: '無制限',
};

const featureOrder = [
  'suggest',
  'searchVolume',
  'relatedKeywords',
  'aiInsights',
  'aiPersonas',
  'personaImages',
];

const UsagePlanCard: React.FC<Props> = ({ usage, loading }) => {
  const features = usage
    ? featureOrder
        .map((key) => [key, usage.features[key]] as const)
        .filter(([, feature]) => Boolean(feature))
    : [];

  return (
    <section className="rounded border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">プランと利用状況</h2>
          <p className="mt-1 text-sm text-gray-500">
            コストが発生する機能の残り回数を確認できます。
          </p>
        </div>
        {usage ? (
          <div className="rounded bg-blue-50 px-3 py-2 text-right">
            <p className="text-sm font-bold text-blue-700">{planLabels[usage.plan] || usage.plan}</p>
            <p className="text-xs font-semibold text-blue-500">
              {scopeLabels[usage.scope] || usage.scope}の利用枠
            </p>
          </div>
        ) : loading ? (
          <span className="text-xs font-semibold text-gray-400">読み込み中...</span>
        ) : null}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {loading && !usage && (
          <div className="rounded border border-dashed border-gray-200 px-3 py-6 text-center text-sm text-gray-500 md:col-span-2 xl:col-span-3">
            利用状況を読み込み中...
          </div>
        )}

        {!loading && !usage && (
          <div className="rounded border border-dashed border-gray-200 px-3 py-6 text-center text-sm text-gray-500 md:col-span-2 xl:col-span-3">
            利用状況を取得できませんでした
          </div>
        )}

        {features.map(([key, feature]) => {
          const used = feature.used || 0;
          const limit = feature.limit || 0;
          const progress = feature.unlimited || !limit
            ? 0
            : Math.min((used / limit) * 100, 100);

          return (
            <div key={key} className="rounded border border-gray-100 bg-slate-50 px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-bold text-gray-800">{feature.label}</p>
                  {feature.coins > 0 && (
                    <p className="mt-1 text-xs font-semibold text-emerald-600">
                      追加コイン {feature.coins}回
                    </p>
                  )}
                </div>
                <p className="shrink-0 text-sm font-bold text-gray-700">
                  {feature.unlimited
                    ? '無制限'
                    : `${feature.remaining ?? 0} / ${feature.limit}`}
                </p>
              </div>
              {!feature.unlimited && (
                <>
                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-white">
                    <div
                      className="h-full rounded-full bg-blue-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    使用済み {used.toLocaleString('ja-JP')}回
                  </p>
                </>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default UsagePlanCard;
